import { Editor } from "@tiptap/react";
import { Button } from "components/DesignSystem/Button/Button";
import { BareInput } from "components/DesignSystem/Input/Input";
import { KEY_ENTER, KEY_ESCAPE } from "constants/keyboardKeys";
import { useState } from "react";

type ComposerLinkInputProps = {
  editor: Editor;
  onClose: () => void;
};

const LINK_MARK = "link";
const URL_SCHEME_REGEX = /^[a-z][a-z\d+.-]*:/i;
const DEFAULT_URL_SCHEME = "https://";

const withScheme = (url: string) =>
  URL_SCHEME_REGEX.test(url) ? url : `${DEFAULT_URL_SCHEME}${url}`;

export const ComposerLinkInput = ({
  editor,
  onClose,
}: ComposerLinkInputProps) => {
  // Focus moves to the URL field, so remember what was selected when it opened
  const [selection] = useState(() => {
    const { from, to } = editor.state.selection;
    return { from, to };
  });
  const [isEditingLink] = useState(() => editor.isActive(LINK_MARK));
  const [url, setUrl] = useState<string>(
    () => editor.getAttributes(LINK_MARK).href ?? ""
  );

  const hasSelection = selection.from !== selection.to;

  const focusSelection = () =>
    editor
      .chain()
      .focus()
      .setTextSelection(selection)
      .extendMarkRange(LINK_MARK);

  const close = () => {
    onClose();
    editor.chain().focus().setTextSelection(selection).run();
  };

  const removeLink = () => {
    onClose();
    focusSelection().unsetLink().run();
  };

  const applyLink = () => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      removeLink();
      return;
    }

    onClose();
    const href = withScheme(trimmedUrl);

    // Nothing selected: drop the URL in as text, chat autolinks it on render
    if (!hasSelection && !isEditingLink) {
      editor
        .chain()
        .focus()
        .setTextSelection(selection)
        .insertContent(href)
        .run();
      return;
    }

    focusSelection().setLink({ href }).run();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setUrl(event.target.value);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KEY_ENTER) {
      event.preventDefault();
      applyLink();
    }

    if (event.key === KEY_ESCAPE) {
      event.preventDefault();
      close();
    }
  };

  return (
    <div className="t-flex t-items-center t-gap-2 sm:t-flex-wrap">
      <BareInput
        autoFocus
        customSize="small"
        placeholder="Paste link here"
        aria-label="Link URL"
        value={url}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      <Button
        type="button"
        customType="primary"
        size="small"
        onClick={applyLink}
      >
        {isEditingLink ? "Update link" : "Add link"}
      </Button>
      {isEditingLink && (
        <Button
          type="button"
          customType="ghost"
          size="small"
          onClick={removeLink}
        >
          Remove
        </Button>
      )}
      <Button type="button" customType="ghost" size="small" onClick={close}>
        Cancel
      </Button>
    </div>
  );
};
